// Permission mapping utility for frontend

export interface UserPermissions {
  canProcessOrders?: boolean
  canManageInventory?: boolean
  canManageUsers?: boolean
  canViewReports?: boolean
  canManageTables?: boolean
  canManageMenu?: boolean
  canProcessRefunds?: boolean
  canViewAnalytics?: boolean
}

// User roles
export type UserRole = 'owner' | 'manager' | 'cashier' | 'waiter' | 'kitchen_staff' | 'admin' | 'restaurant_owner'

// Role-based menu visibility
export const roleMenuAccess: Record<string, string[]> = {
  // Kitchen staff only sees orders (confirmed orders)
  kitchen_staff: ['kitchen-orders'],

  // Waiters only see ready orders
  waiter: ['waiter-orders'],

  // Cashiers see POS and orders
  cashier: ['orders', 'pos', 'delivery-orders', 'tables'],

  // Managers see most things except settings
  manager: ['dashboard', 'orders', 'food-items', 'tables', 'analytics', 'food-items-performance', 'reports', 'pos', 'delivery-orders', 'users'],

  // Admin/Owner sees everything
  admin: ['all'],
  owner: ['all'],
  restaurant_owner: ['all'],
}

// Map routes to required permissions
export const routePermissions: Record<string, string[]> = {
  // Dashboard - accessible to managers and above
  dashboard: [],

  // Food Items
  'food-items': ['canManageMenu'],

  // Tables
  'tables': ['canManageTables'],

  // Orders
  'orders': ['canProcessOrders'],

  // Kitchen Orders - for kitchen staff
  'kitchen-orders': [],

  // Waiter Orders - for waiters
  'waiter-orders': [],

  // Analytics
  'analytics': ['canViewAnalytics'],
  'food-items-performance': ['canViewAnalytics'],

  // Reports
  'reports': ['canViewReports'],

  // POS System
  'pos': ['canProcessOrders'],
  'delivery-orders': ['canProcessOrders'],

  // Staff Management
  'users': ['canManageUsers'],

  // Settings - only restaurant owners
  'settings': [],
  'profile': [],
}

/**
 * Check if user role has access to a specific menu item
 * @param route - Route name
 * @param role - User role
 * @param isOwner - Whether user is restaurant owner
 * @returns boolean
 */
export const hasRoleAccess = (
  route: string,
  role: string,
  isOwner: boolean = false
): boolean => {
  // Owners have access to everything
  if (isOwner || role === 'owner' || role === 'restaurant_owner' || role === 'admin') {
    return true
  }

  const allowedRoutes = roleMenuAccess[role] || []

  // Check if role has 'all' access
  if (allowedRoutes.includes('all')) {
    return true
  }

  return allowedRoutes.includes(route)
}

/**
 * Get menu items for a specific role
 * @param role - User role
 * @param isOwner - Whether user is restaurant owner
 * @returns Array of accessible route names
 */
export const getMenuItemsForRole = (
  role: string,
  isOwner: boolean = false
): string[] => {
  if (isOwner || role === 'owner' || role === 'restaurant_owner' || role === 'admin') {
    return Object.keys(routePermissions)
  }

  return roleMenuAccess[role] || []
}

/**
 * Check if user has permission to access a route
 * @param route - Route name (e.g., 'food-items')
 * @param permissions - User permissions object
 * @param isOwner - Whether user is restaurant owner
 * @returns boolean
 */
export const hasRoutePermission = (
  route: string,
  permissions: UserPermissions = {},
  isOwner: boolean = false
): boolean => {
  // Restaurant owners have access to all routes
  if (isOwner) {
    return true
  }

  // Get required permissions for this route
  const requiredPermissions = routePermissions[route] || []

  // If no permissions required, allow access
  if (requiredPermissions.length === 0) {
    return true
  }

  // Check if user has at least one of the required permissions
  return requiredPermissions.some(
    (permission) => permissions[permission as keyof UserPermissions] === true
  )
}

/**
 * Get all accessible routes for a user
 * @param permissions - User permissions object
 * @param isOwner - Whether user is restaurant owner
 * @returns Array of accessible route names
 */
export const getAccessibleRoutes = (
  permissions: UserPermissions = {},
  isOwner: boolean = false
): string[] => {
  if (isOwner) {
    return Object.keys(routePermissions)
  }

  return Object.keys(routePermissions).filter((route) =>
    hasRoutePermission(route, permissions, isOwner)
  )
}

/**
 * Check if user has a specific permission
 * @param permission - Permission name
 * @param permissions - User permissions object
 * @param isOwner - Whether user is restaurant owner
 * @returns boolean
 */
export const hasPermission = (
  permission: keyof UserPermissions,
  permissions: UserPermissions = {},
  isOwner: boolean = false
): boolean => {
  if (isOwner) {
    return true
  }

  return permissions[permission] === true
}


